/**
 * Display-name helpers for the refunds surface (AUT-129). Mirrors the
 * payouts / orders versions — kept page-local so the refunds page never
 * imports across sibling vertical boundaries.
 *
 * The server sends `recipientName` already redacted-or-full per
 * `piiRevealed`. These helpers only derive avatar initials + a truncated
 * provider-refund id from values the SPA already holds — they never
 * reconstruct PII. Cyrillic-aware so "Алексей Петров" renders as "АП".
 */

/** Two-letter avatar initials from a display name; '—' fallback. */
export const initials = (name: string | null): string => {
  if (!name) return '—';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0];
  if (!first) return '—';
  const second = parts[1];
  if (!second) return first.slice(0, 2).toLocaleUpperCase();
  return ((first[0] ?? '') + (second[0] ?? '')).toLocaleUpperCase();
};

/**
 * Truncate a provider-refund id to the mockup's `rfnd_a91c…03f` shape.
 * Short ids (or those without enough chars to truncate meaningfully) are
 * returned as-is.
 */
export const shortProviderRefundId = (id: string): string => {
  if (id.length <= 12) return id;
  return `${id.slice(0, 9)}…${id.slice(-3)}`;
};

/** Truncate an order id to a compact `ord-2891…000` reference. */
export const shortId = (id: string): string => {
  if (id.length <= 11) return id;
  return `${id.slice(0, 8)}…${id.slice(-3)}`;
};
